// P7 — patient-facing AI summary view. Reads GET /my-summaries (backend,
// this same app) — genuinely new, no proactive summary view existed here
// before this (only the reactive chatbot Q&A and TrendsPanel's numeric
// charts). Shows the AI summary TEXT per completed test, not stat tiles.
//
// Proove-parity build, Part 7 — added structured dial-style rendering for
// rows that now carry stats_json/stats_extraction_status (the backend
// /my-summaries route decrypts stats_json before it ever reaches here —
// see that route's own comment). Dial pattern ported inline (not
// imported) from prooveai-portal-v2/frontend/src/components/reports/
// ScoreDial.js + reportHelpers.js's levelColor export — StatDial/
// STAT_LABELS are NOT separately exported anywhere in that source, so the
// visual pattern is copied here rather than importing a module that
// doesn't exist. Every card also now shows a patient-identity header
// (name, DOB, upload date, uploaded-by) — the same "this is definitely
// the same person" visual safeguard already established elsewhere in
// this build, on top of (not instead of) the server-side identity_id
// match this page's own query already enforces.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Chip, Alert } from "@mui/material";
import { Science, LocalHospital, PersonOutline, ArrowBack, HourglassEmpty } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import { useContext } from "react";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };
// 2026-10-22 addition — same pre-check + response-field dual gate
// MyResults.js/MyPatientsResults.js already use for their own flag
// (REACT_APP_MY_RESULTS_ENABLED, shared by both of those pages).
const MY_SUMMARIES_ENABLED = process.env.REACT_APP_MY_SUMMARIES_ENABLED === "true";

// ── Dial-style stats rendering (ported inline pattern — see file banner) ──

// Priority order here MUST exactly match ScoreDial.js/reportHelpers.js in
// prooveai-portal-v2 — compound phrases checked before the bare words, so
// a value like "Moderately Low" doesn't light up one color on the dial
// and a different one on a label pill elsewhere on this same card.
function levelColor(level) {
  if (!level) return "#575555";
  const l = String(level).toLowerCase();
  if (l.includes("moderately low") || l.includes("mildly low")) return "#1a7a3c";
  if (l.includes("moderately high") || l.includes("mildly high")) return "#b3362c";
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative")) return "#b3362c";
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution")) return "#a1750f";
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition")) return "#1a7a3c";
  if (l.includes("hps")) return "#b3362c";
  if (l.includes("aps")) return "#a1750f";
  if (l.includes("lps")) return "#1a7a3c";
  return "#637cdb";
}

function bandIndexForLevel(level) {
  const l = String(level || "").toLowerCase();
  if (l.includes("moderately low") || l.includes("mildly low")) return 0;
  if (l.includes("moderately high") || l.includes("mildly high")) return 2;
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative")) return 2;
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution")) return 1;
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition")) return 0;
  if (l.includes("hps")) return 2;
  if (l.includes("aps")) return 1;
  if (l.includes("lps")) return 0;
  return null;
}

const _DIAL_BANDS = [
  { angles: [180, 120], color: "#22a355" },
  { angles: [120, 60], color: "#e0a000" },
  { angles: [60, 0], color: "#d13b3b" },
];
const _NEEDLE_DEG = [150, 90, 30];

function _polarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

function StatDial({ level, score, size = 92 }) {
  const idx = bandIndexForLevel(level);
  const strokeWidth = 7;
  const cx = size / 2;
  const r = size / 2 - 10;
  const cy = strokeWidth + r;
  const needleColor = idx == null ? "#9a9a9a" : _DIAL_BANDS[idx].color;
  const needleDeg = idx == null ? 90 : _NEEDLE_DEG[idx];
  const [nx, ny] = _polarToXY(cx, cy, r - 10, needleDeg);
  const numberY = cy + 24;
  const totalHeight = numberY + 22;
  return (
    <svg width={size} height={totalHeight} viewBox={`0 0 ${size} ${totalHeight}`} role="img"
      aria-label={level ? `${level} risk dial` : "risk dial"}>
      {_DIAL_BANDS.map((band, i) => {
        const [x0, y0] = _polarToXY(cx, cy, r, band.angles[0]);
        const [x1, y1] = _polarToXY(cx, cy, r, band.angles[1]);
        return (
          <path key={i} d={`M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`}
            stroke={band.color} strokeWidth={strokeWidth} strokeLinecap="round" fill="none"
            opacity={idx === i ? 1 : 0.55} />
        );
      })}
      <circle cx={cx} cy={cy} r={3} fill="#575555" />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={needleColor} strokeWidth={2.5} strokeLinecap="round" />
      {score != null && (
        <text x={cx} y={numberY} textAnchor="middle" dominantBaseline="hanging"
          fontSize={16} fontWeight={800} fill={levelColor(level)}>
          {score}
        </text>
      )}
    </svg>
  );
}

// Which stats_json fields get a dial, per report_type_id (labariumaireport's
// own proove_test_id values — confirmed in _PROOVE_STATS_REPORT_TYPES
// there). Each entry is {field, label} — field is a key straight off the
// (already-decrypted) stats_json object, shaped as {score, level, ...}
// per _typed_normalize_extraction's own documented shapes.
const STAT_LABELS = {
  proove_opioid_risk: [
    { field: "proove_ori", label: "Opioid Risk Index" },
    { field: "ort", label: "Opioid Risk Tool (ORT)" },
    { field: "phq2", label: "PHQ-2" },
  ],
  proove_pain_perception: [
    { field: "self_reported_pain", label: "Self-Reported Pain" },
    { field: "pss", label: "Perceived Stress Scale" },
  ],
};

function DialRow({ stats, reportTypeId, isDark }) {
  const fields = STAT_LABELS[reportTypeId] || [];
  const present = fields.filter((f) => stats && stats[f.field] && (stats[f.field].score || stats[f.field].level));
  const comt = reportTypeId === "proove_pain_perception" ? stats?.comt_predisposition : null;
  const hasGenes = reportTypeId === "proove_pgx" && Array.isArray(stats?.genes) && stats.genes.length > 0;
  if (!present.length && !comt && !hasGenes) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2.5, pb: 2.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
      {present.map((f) => (
        <Box key={f.field} sx={{ textAlign: "center" }}>
          <StatDial score={stats[f.field].score} level={stats[f.field].level} size={84} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, maxWidth: 110 }}>
            {f.label}
          </Typography>
          {stats[f.field].level && (
            <Chip label={stats[f.field].level} size="small"
              sx={{ mt: 0.5, fontSize: "0.65rem", fontWeight: 700, color: levelColor(stats[f.field].level),
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }} />
          )}
        </Box>
      ))}
      {comt && comt.level && (
        <Box sx={{ textAlign: "center" }}>
          <StatDial score={null} level={comt.level} size={84} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, maxWidth: 110 }}>
            Genetic Pain Sensitivity (COMT)
          </Typography>
          <Chip label={comt.level} size="small"
            sx={{ mt: 0.5, fontSize: "0.65rem", fontWeight: 700, color: levelColor(comt.level),
              bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }} />
        </Box>
      )}
      {reportTypeId === "proove_pgx" && Array.isArray(stats?.genes) && stats.genes.length > 0 && (
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
            Gene Findings
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {stats.genes.slice(0, 12).map((g, i) => (
              <Chip key={i} label={`${g.gene}${g.phenotype ? `: ${g.phenotype}` : ""}`} size="small"
                sx={{ fontSize: "0.68rem", color: isDark ? "#cbd5e1" : "#334155",
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Part 10 — structured results table for Labarium-native reports ──
// (tox/blood/molecular). Explicitly NOT dial-based (see plan doc Part 10)
// — just a flagged-results table + full-results table, reusing the same
// red/green status convention report_builder.py already applies to the
// PDF (status-normal/status-abnormal, #cc0000 for abnormal) so the web
// card and the PDF read consistently.
//
// HARD SCOPE: only ever rendered for report_kind === "patient" rows (see
// the call site below) — the backend (/my-summaries, labariumaireport's
// upload_report_to_gcs) already enforces this same scoping independently,
// this is defense-in-depth on the render side too, per the open-exposure
// note recorded in the plan doc 2026-10-16: whether patients should see
// clinician-flavor native-report content at all is a CEO call not yet
// made, and this build must not deepen that exposure either way.
const _ABNORMAL_STATUSES = new Set(["high", "low", "abnormal", "detected"]);
const _NORMAL_STATUSES = new Set(["normal"]);
function _isAbnormalValue(v) {
  if (typeof v === "boolean") return v === true; // e.g. drug-screen validity's "Flagged" boolean
  const s = String(v ?? "").trim().toLowerCase();
  if (_ABNORMAL_STATUSES.has(s)) return true;
  // report_builder.py's own _render_tox_detail_df flags exactly this
  // combination (medication_comparison in review/illicit, or comment ==
  // positive) — matched here so the web card and the PDF agree on which
  // tox-detail rows read as abnormal.
  return s === "illicit" || s === "review" || s === "positive";
}
function _isNormalValue(v) {
  if (typeof v === "boolean") return v === false;
  return _NORMAL_STATUSES.has(String(v ?? "").trim().toLowerCase());
}

// Column keys that should get the red/green status treatment when present.
// Advisor finding, pre-integration: capital-"Comment" (drug-screen
// consistent/second-section tables) removed — report_builder.py's PDF
// never colors that column (see _render_drug_screen_consistent_df); a
// literal "Positive" there (meaning an expected/prescribed med was
// detected as expected) would render red to a patient, the opposite of
// what it means. Lowercase tox-detail `comment` stays — report_builder's
// _render_tox_detail_df DOES flag `comment.lower() == "positive"` there.
const _STATUS_LIKE_KEYS = new Set(["status", "Flagged", "comment", "medication_comparison"]);
const _SENTINEL_EMPTY_SECTION = "__EMPTY__"; // flatten_drug_screen_consistent's own "nothing in this section" placeholder row

function ResultsTable({ rows, isDark, emptyText }) {
  // Security-auditor finding (defense in depth, second layer — the
  // backend's own upload-time shape validator, _is_valid_structured_
  // results_shape, is the primary guard) — this row data is decrypted
  // server-side from a value that, by the time it reaches this component,
  // already went through that validator, but never trust a single layer
  // for PHI-adjacent rendered content: a non-array `rows` (or a non-dict
  // item inside it) must degrade to the empty state, never crash this
  // card (there is no error boundary anywhere in this app, so an
  // unguarded `.map` here would take down the patient's entire My
  // Summaries page, not just this one card).
  const safeRows = Array.isArray(rows) ? rows.filter((r) => r && typeof r === "object" && !Array.isArray(r)) : [];
  if (!safeRows.length) {
    return (
      <Typography sx={{ fontSize: "0.82rem", fontStyle: "italic", color: isDark ? "#64748b" : "#94a3b8", mb: 1.5 }}>
        {emptyText}
      </Typography>
    );
  }
  const rowsToRender = safeRows;
  // Columns driven by whichever keys are actually present (these DataFrames'
  // shapes genuinely differ per report type — see ocr_engine.py's own
  // flatten_* functions) rather than a hardcoded column list.
  const cols = Object.keys(rowsToRender[0]).filter((k) => k !== "section" && k !== "Section");
  const sectionKey = Object.keys(rowsToRender[0]).includes("section") ? "section" : (Object.keys(rowsToRender[0]).includes("Section") ? "Section" : null);
  return (
    <Box sx={{ overflowX: "auto", mb: 2 }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
        <Box component="thead">
          <Box component="tr">
            {sectionKey && (
              <Box component="th" sx={{ textAlign: "left", py: 0.75, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.12)", color: isDark ? "#94a3b8" : "#64748b", fontWeight: 700 }}>
                Section
              </Box>
            )}
            {cols.map((h) => (
              <Box component="th" key={h} sx={{ textAlign: "left", py: 0.75, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.12)", color: isDark ? "#94a3b8" : "#64748b", fontWeight: 700, whiteSpace: "nowrap" }}>
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rowsToRender.map((row, i) => {
            // flatten_drug_screen_consistent's own sentinel for "nothing in
            // this category" — report_builder.py's PDF renders this as a
            // "— No results in this category —" placeholder row, never the
            // literal internal marker string. Same treatment here.
            const isEmptySentinel = Object.values(row).includes(_SENTINEL_EMPTY_SECTION) && (row.Drug === _SENTINEL_EMPTY_SECTION);
            if (isEmptySentinel) {
              return (
                <Box component="tr" key={i}>
                  {sectionKey && (
                    <Box component="td" sx={{ py: 0.6, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)", color: isDark ? "#cbd5e1" : "#334155" }}>
                      {String(row[sectionKey] ?? "")}
                    </Box>
                  )}
                  <Box component="td" colSpan={cols.length} sx={{
                    py: 0.6, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)",
                    fontStyle: "italic", color: isDark ? "#64748b" : "#94a3b8",
                  }}>
                    — No results in this category —
                  </Box>
                </Box>
              );
            }
            return (
              <Box component="tr" key={i}>
                {sectionKey && (
                  <Box component="td" sx={{ py: 0.6, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)", color: isDark ? "#cbd5e1" : "#334155" }}>
                    {String(row[sectionKey] ?? "")}
                  </Box>
                )}
                {cols.map((k) => {
                  const isStatusLike = _STATUS_LIKE_KEYS.has(k);
                  const flagged = isStatusLike && _isAbnormalValue(row[k]);
                  const normal = isStatusLike && !flagged && _isNormalValue(row[k]);
                  return (
                    <Box component="td" key={k} sx={{
                      py: 0.6, px: 1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,53,122,0.06)",
                      color: flagged ? "#cc0000" : (normal ? (isDark ? "#4ade80" : "#1a7a1a") : (isDark ? "#cbd5e1" : "#334155")),
                      fontWeight: (flagged || normal) ? 700 : 400,
                    }}>
                      {typeof row[k] === "boolean" ? (row[k] ? "Yes" : "No") : String(row[k] ?? "")}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// Advisor finding, pre-integration: the plan doc's own spec says "a small
// summary badge at the top of each card" — singular "a card," applies to
// every structured-results kind, not just "standard," and belongs at the
// TOP (right after IdentityHeader), not buried below the AI summary text
// where the tables themselves now sit (see the ui-ux reorder below). Split
// out so the card-level render site can place it independently of the
// (now summary-text-deferred) tables.
function FlaggedBadge({ data, isDark }) {
  if (!data || data.flagged_count == null || data.total_count == null) return null;
  const { flagged_count: flaggedCount, total_count: total } = data;
  return (
    <Chip
      label={`${flaggedCount} flagged out of ${total}`}
      size="small"
      sx={{
        mb: 1.5, fontWeight: 700, fontSize: "0.72rem",
        bgcolor: flaggedCount > 0 ? "rgba(204,0,0,0.1)" : "rgba(26,122,26,0.1)",
        color: flaggedCount > 0 ? "#cc0000" : (isDark ? "#4ade80" : "#1a7a1a"),
      }}
    />
  );
}

function StructuredResultsCard({ data, isDark }) {
  if (!data || !data.kind) return null;

  if (data.kind === "standard") {
    const flagged = data.flagged || [];
    return (
      <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
        {flagged.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
              Flagged / Out-of-Range Results
            </Typography>
            <ResultsTable rows={flagged} isDark={isDark} emptyText="" />
          </>
        )}
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
          Full Results
        </Typography>
        <ResultsTable rows={data.full_results || []} isDark={isDark} emptyText="No results available." />
      </Box>
    );
  }

  if (data.kind === "tox") {
    // ui-ux-checker finding — a clean (nothing-flagged) tox report used to
    // show every sub-table's bolded header followed by an italic "nothing
    // found" line regardless of content, reading like a checklist of
    // possible bad news rather than reassurance (denser than the dial
    // pattern this replaces for Proove-family tests). Same "hide when
    // empty" treatment the "standard" kind's own flagged table already
    // gets — "Full Detail Results" always shows (it's the comprehensive
    // table, not a conditional finding).
    const first = data.first_page_summary || [];
    const second = data.second_section || [];
    return (
      <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
        {first.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
              Prescribed Medications Summary
            </Typography>
            <ResultsTable rows={first} isDark={isDark} emptyText="" />
          </>
        )}
        {second.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
              Non-Prescribed / Illicit Findings
            </Typography>
            <ResultsTable rows={second} isDark={isDark} emptyText="" />
          </>
        )}
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
          Full Detail Results
        </Typography>
        <ResultsTable rows={data.full_results || []} isDark={isDark} emptyText="No detail results available." />
      </Box>
    );
  }

  if (data.kind === "drug_screen") {
    const consistent = data.consistent || [];
    const validity = data.validity || [];
    return (
      <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
        {consistent.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
              Drug Screen Summary
            </Typography>
            <ResultsTable rows={consistent} isDark={isDark} emptyText="" />
          </>
        )}
        {validity.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
              Specimen Validity
            </Typography>
            <ResultsTable rows={validity} isDark={isDark} emptyText="" />
          </>
        )}
        {data.validity_note && (
          <Typography sx={{ fontSize: "0.78rem", fontStyle: "italic", color: isDark ? "#94a3b8" : "#64748b", mb: 1.5 }}>
            {data.validity_note}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.75 }}>
          Full Detail Results
        </Typography>
        <ResultsTable rows={data.full_results || []} isDark={isDark} emptyText="No detail results available." />
      </Box>
    );
  }

  return null;
}

// Identity header — mandatory on every card (name, DOB, upload date,
// uploaded-by), same visual "this is definitely the same person"
// safeguard already established elsewhere in this build, alongside (not
// instead of) the server-side identity_id match the /my-summaries query
// already enforces.
function IdentityHeader({ r, isDark }) {
  const name = [r.patient_first_name, r.patient_last_name].filter(Boolean).join(" ");
  return (
    <Box sx={{
      display: "flex", flexWrap: "wrap", gap: 2, mb: 2, px: 1.5, py: 1,
      borderRadius: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)",
      fontSize: "0.74rem", color: isDark ? "#94a3b8" : "#64748b",
    }}>
      {name && <span><b style={{ color: isDark ? "#cbd5e1" : "#334155" }}>{name}</b></span>}
      {r.patient_dob && <span>DOB: {r.patient_dob}</span>}
      <span>Uploaded: {formatEST(r.generated_at, { dateOnly: true })}</span>
      {/* 2026-10-24 fix (ui-ux-checker finding) — "By:" read ambiguously
          right next to the new "Ordered by:" span (which one is the lab
          staff uploader vs. the ordering clinician?). Relabeled to
          disambiguate; no change to which field is shown. */}
      {r.generated_by && <span>Uploaded by: {r.generated_by}</span>}
      {/* Practice + provider name are fine to show to the patient (already
          "directory-style public-record info" per the backend's own
          convention — see /my-summaries' field comments); NPI and the
          provider's personal phone are deliberately never shown here. */}
      {r.provider_name && (
        <span>Ordered by: {r.provider_name}{r.practice_name ? ` — ${r.practice_name}` : ""}</span>
      )}
    </Box>
  );
}

export default function MySummaries() {
  // 2026-10-13 fix — this page hardcoded light-mode colors everywhere
  // despite rendering a working ThemeToggle — useTheme()/isDark were
  // entirely absent here, same gap as Settings.js/MyPatients.js/
  // OrderTest.js. Same useTheme() pattern Login.js/Register.js use.
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext); // real shape confirmed from LabReports.js: user?.token, not a separate `token` field
  const token = user?.token;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 2026-10-21 fix (issue checklist #24) — /my-summaries hard-caps at 100
  // rows with no signal of anything cut off; a patient with >100 reports
  // silently lost visibility into older ones. The backend now returns
  // total_count/truncated alongside results — surfaced here as a plain,
  // honest banner rather than silent truncation.
  const [totalCount, setTotalCount] = useState(null);
  const [truncated, setTruncated] = useState(false);
  // 2026-10-22 addition — same disabled-state handling as MyResults.js/
  // MyPatientsResults.js's own feature_disabled convention: a pre-check
  // (so an already-known-off flag never even fires the request) AND a
  // response-field check (defense in depth, matches the sibling pages).
  const [featureDisabled, setFeatureDisabled] = useState(!MY_SUMMARIES_ENABLED);

  useEffect(() => {
    if (!MY_SUMMARIES_ENABLED) { setLoading(false); return; }
    axios.get(`${API_BASE}/my-summaries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data?.feature_disabled) { setFeatureDisabled(true); return; }
        setResults(res.data.results || []);
        setTotalCount(typeof res.data.total_count === "number" ? res.data.total_count : null);
        setTruncated(!!res.data.truncated);
      })
      .catch(() => setError("Unable to load your summaries right now."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        {/* 2026-10-04 — this page never had a back-to-Home link, unlike its
            own stated precedent LabReports.js (which has one at /home).
            Same gap found and fixed on labariumaireport's own requisition
            page this same pass. */}
        <Box onClick={() => navigate("/home")}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6,
            transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Summaries</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 5 }}>
        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 4 }}>
          A plain-language summary of each of your completed tests, as soon as they're ready.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : featureDisabled ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>My Summaries isn't available yet.</Alert>
        ) : error ? (
          <Typography sx={{ color: "#ef4444", textAlign: "center", py: 6 }}>{error}</Typography>
        ) : results.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <Science sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>No summaries yet — check back once your first test is complete.</Typography>
          </Box>
        ) : (
          <>
            {truncated && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Showing your most recent {results.length} of {totalCount} reports. Contact
                info@labarium.com if you need an older one not shown here.
              </Alert>
            )}
          {results.map((r) => (
            <Box key={r._id} sx={{
              mb: 3, borderRadius: 3, overflow: "hidden",
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
              bgcolor: isDark ? "#111827" : "#fff",
              boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
            }}>
              <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`, display: "flex", alignItems: "center", gap: 1.5 }}>
                {r.report_kind === "patient" ? <PersonOutline sx={{ color: BRAND.gold, fontSize: 18 }} /> : <LocalHospital sx={{ color: BRAND.gold, fontSize: 18 }} />}
                <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5, textTransform: "capitalize" }}>{r.report_type_id}</Typography>
                {/* 2026-10-04 fix — real bug: slicing the raw UTC string's first 10
                    chars gives the UTC calendar date, wrong near midnight EST (an
                    11pm EST report is already "tomorrow" in UTC). */}
                <Chip label={formatEST(r.generated_at, { dateOnly: true })} size="small" sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem" }} />
              </Box>
              <Box sx={{ p: 3 }}>
                <IdentityHeader r={r} isDark={isDark} />
                {r.report_kind === "patient" && <FlaggedBadge data={r.structured_results} isDark={isDark} />}
                <DialRow stats={r.stats_json} reportTypeId={r.report_type_id} isDark={isDark} />
                {r.stats_extraction_status === "pending" ? (
                  // Mandatory pending state — a clear "still generating"
                  // card, not a broken/empty-looking one. Only ever set
                  // on a Proove row (see labariumaireport's
                  // _write_report_summary_doc — every other caller leaves
                  // this field unset/None).
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 2, color: isDark ? "#94a3b8" : "#64748b" }}>
                    <HourglassEmpty sx={{ fontSize: 20, opacity: 0.7 }} />
                    <Typography sx={{ fontStyle: "italic", fontSize: "0.9rem" }}>
                      Your summary is still being generated — check back shortly.
                    </Typography>
                  </Box>
                ) : r.ai_summary ? (
                  <Box sx={{ fontSize: "0.92rem", color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, "& p": { mb: 1.5 }, "& ul": { pl: 3 } }}
                    dangerouslySetInnerHTML={{ __html: r.ai_summary }} />
                ) : (
                  <Typography sx={{ color: isDark ? "#64748b" : "#AAA", fontStyle: "italic" }}>Summary not yet available for this test.</Typography>
                )}
                {/* Part 10 — structured flagged/full results table, Labarium-native
                    reports only. Hard-scoped to report_kind === "patient" here too
                    (not just on the backend) — see StructuredResultsCard's own
                    comment on the open-exposure constraint this must not deepen.
                    ui-ux-checker findings: (1) placed AFTER the plain-language AI
                    summary, not before it — leading with raw tabular clinical data
                    ahead of the interpretive text reads as backwards for comfort to
                    a non-clinical reader. (2) gated on stats_extraction_status !==
                    "pending" — cheap insurance against ever showing a full results
                    table above a "still being generated" message, consistent with
                    the Array.isArray defensive-guard discipline already applied
                    elsewhere in this file, even though this field is documented as
                    Proove-row-only today. */}
                {r.report_kind === "patient" && r.stats_extraction_status !== "pending" && (
                  <StructuredResultsCard data={r.structured_results} isDark={isDark} />
                )}
              </Box>
            </Box>
          ))}
          </>
        )}
      </Box>
    </Box>
  );
}
